import React, { useEffect, useRef } from 'react';
import { Ban as Bandage, X, Clock } from 'lucide-react';
import { MyTeamsModal, useMyTeamsAnchoredModal } from '../MyTeams/MyTeamsModal';

interface InjuryDescriptionModalProps {
  show: boolean;
  onClose: () => void;
  playerName: string;
  description: string;
  language: 'en' | 'fr';
  daysRemaining?: number;
  isReturned?: boolean;
}

export function InjuryDescriptionModal({
  show,
  onClose,
  playerName,
  description,
  language,
  daysRemaining,
  isReturned
}: InjuryDescriptionModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const anchoredModal = useMyTeamsAnchoredModal();

  useEffect(() => {
    if (show && !anchoredModal && modalRef.current) {
      // 👇 On force le scroll vers l'élément
      modalRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [show, anchoredModal]);

  if (!show) return null;

  const modalTitle = language === 'fr' ? 'Description de la blessure' : 'Injury Description';
  const closeText = language === 'fr' ? 'Fermer' : 'Close';
  const returnedText = language === 'fr' ? 'Joueur apte à retourner au jeu' : 'Player ready to return to play';
  const daysRemainingText = language === 'fr' 
    ? `Jours restants pour réactiver : ${daysRemaining}`
    : `Days remaining to reactivate: ${daysRemaining}`;

  return (
    <MyTeamsModal onClose={onClose}>
      <div 
        className="modal fade show" 
        style={{ 
          display: 'block',
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 1050,
          overflow: 'auto'
        }} 
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
      >
        <div className="modal-dialog modal-dialog-centered" ref={modalRef}>
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title d-flex align-items-center text-white">
                {isReturned ? (
                  <Clock size={18} className="text-warning me-2" />
                ) : (
                  <Bandage size={18} className="text-danger me-2" />
                )}
                {modalTitle}
              </h5>
              <button 
                type="button" 
                className="btn-close btn-close-white" 
                onClick={onClose}
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body text-white">
              <div className="mb-3">
                <strong className="text-white">{playerName}</strong>
              </div>
              
              {isReturned ? (
                <div className="alert alert-warning">
                  <div className="d-flex align-items-center mb-2">
                    <Clock size={16} className="text-warning me-2" />
                    <strong className="text-dark">{returnedText}</strong>
                  </div>
                  <p className="mb-0 text-dark">{daysRemainingText}</p>
                  <p className="mt-2 mb-0 small text-dark">
                    {language === 'fr' 
                      ? "Vous devez réactiver ce joueur dans le délai indiqué, sinon il sera automatiquement libéré et deviendra agent libre."
                      : "You must reactivate this player within the specified timeframe, otherwise they will automatically be released and become a free agent."}
                  </p>
                </div>
              ) : (
                <p className="text-white">{description || (language === 'fr' ? 'Aucune description disponible' : 'No description available')}</p>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
              >
                {closeText}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div 
        className="modal-backdrop fade show" 
        style={{ 
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 1040 
        }}
      ></div>
    </MyTeamsModal>
  );
}